import { uninitialised } from "helpers/uninitialised";
import {
  ENGLISH,
  ENGLISH_SPELLED_OUT,
  FRANÇAIS,
  FRANÇAIS_SPELLED_OUT,
  PREFERRED_LANGUAGE,
} from "locales/constants";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const languageOptions: Record<string, string> = {
  [ENGLISH]: ENGLISH_SPELLED_OUT,
  [FRANÇAIS]: FRANÇAIS_SPELLED_OUT,
};

export interface ILanguageContext {
  language: string;
  changeLanguage: (lang: string) => void;
  languageOptions: Record<string, string>;
}

const INITIAL_STATE: ILanguageContext = {
  language: ENGLISH,
  changeLanguage: uninitialised,
  languageOptions,
};

export const LanguageContext =
  React.createContext<ILanguageContext>(INITIAL_STATE);

export const useLanguage = () => useContext(LanguageContext);

export const LanguageConsumer = LanguageContext.Consumer;

interface ILanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<ILanguageProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation("ui");

  const getStoredLanguage = () => {
    return localStorage.getItem(PREFERRED_LANGUAGE) || i18n.language;
  };

  const [language, setLanguage] = useState<string>(getStoredLanguage);

  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
      setLanguage(storedLang);
    }
  }, [i18n]);

  const changeLanguage = (lang: string) => {
    if (lang !== language) {
      i18n.changeLanguage(lang);
      setLanguage(lang);
      localStorage.setItem(PREFERRED_LANGUAGE, lang);
    }
  };
  return (
    <LanguageContext.Provider
      value={{ language, changeLanguage, languageOptions }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
